import { ReactElement, useEffect, useRef, useState } from "react";
import { cloneDeep } from "lodash";
import { Box } from "@twilio-paste/core/box";

import { getConfig } from "~/org/twilio/one-support-center/shared/osc-env";
import {
  MessageBlock,
  WiseOwlBlock,
  WiseOwlGetChatSummaryByIdResponseData,
  WiseOwlSyncDocumentContent,
  wiseOwlViewCopy,
  trackingDetails,
  NewAiMessageStatus,
  SendMessageFormValues,
  CustomerFeedbackFormValues,
  WiseOwlLoadingConfiguration,
  WiseOwlProps,
  WiseOwlEventEnum,
  MessageCompletionStatus,
  WiseOwlBlockType,
  MessageBlockRole,
} from "@twiliointernal/org/twilio/e2-commons/shared/definitions";
import {
  useScript,
  useTwilioSyncDocument,
} from "@twiliointernal/org/twilio/e2-commons/shared/hooks";

import { constants } from "./definitions";
import { LegalDisclaimer } from "./sub/legalDisclaimer";
import WiseOwlChat from "../WiseOwlChat";
import {
  useCreateChat,
  useSendMessage,
  useSubmitFeedback,
  useGetChatSummary,
  useGetChat,
  WiseOwlProvider,
  useSessionStorage,
} from "../../hooks";
import {
  createNewUserMessageBlock,
  findLastFailedUserMessage,
  findUserPromptMessageToRegenerateAssistantMessage,
  setUserMessageBlockFailed,
  spliceBlocks,
} from "../../components/blockRenderer/helpers";
import { SESSION_KEY } from "../../components/blockRenderer/definitions/constants";
const { recaptchaSiteKey } = getConfig();

const WiseOwl = (props: WiseOwlProps): ReactElement => {
  const {
    ingress,
    chatId: chatIdFromProps,
    onEvent,
    onError,
    onTrackEvent,
    user,
    stargateUngatedUserId,
  } = props;

  const [loadingConfiguration, updateLoadingConfiguration] =
    useState<WiseOwlLoadingConfiguration>(constants.defaultLoadingState);

  const chatId = useRef<string | undefined>(chatIdFromProps);
  const [interruptedChatId, setInterruptedChatId] = useSessionStorage<
    string | undefined
  >(SESSION_KEY.INTERRUPTED_CHAT_ID);

  const [newAiMessageStatus, setNewAiMessageStatus] =
    useState<NewAiMessageStatus>(NewAiMessageStatus.None);
  const [blocks, setBlocks] = useState<WiseOwlBlock[]>([]);
  const previousBlocks = useRef<WiseOwlBlock[]>([]);
  const [failedUserMessageId, setFailedUserMessageId] = useState<string>();
  const [disableChatMessageSubmission, setChatSubmissionDisabled] =
    useState<boolean>();

  const { mutateAsync: createChatMutation } = useCreateChat(props);
  const { mutateAsync: sendMessageMutation } = useSendMessage(props);
  const { mutateAsync: createFeedbackMutation } = useSubmitFeedback(props);
  const { mutateAsync: getChatMutation } = useGetChat(props);
  const { mutateAsync: getChatSummaryMutation } = useGetChatSummary(props);

  /**
   * Attach Recaptcha script if it's not already attached
   * in the consuming application
   */
  const isRecaptchaAttached = !!document.querySelector(
    'script[src*="https://www.google.com/recaptcha/api"]'
  );
  useScript({
    disabled: isRecaptchaAttached,
    src: `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}&badge=bottomleft`,
  });

  useEffect(() => {
    /**
     * Fetch a chat on load if a chatId is present
     */
    const fetchChatOnMount = async (): Promise<void> => {
      const currentChatId = chatIdFromProps || interruptedChatId;
      if (currentChatId) {
        chatId.current = currentChatId;

        await handleGetChat(chatId.current, interruptedChatId);
      }
    };

    fetchChatOnMount();
  }, []);

  useEffect(() => {
    /**
     * Incoming chatIdFromProps, update state and fetch
     * - Do not fetch if submitting a new chat
     */
    if (
      !chatId.current &&
      chatIdFromProps &&
      !loadingConfiguration.submission
    ) {
      chatId.current = chatIdFromProps;
      handleGetChat(chatIdFromProps);
      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        fetchChatById: true,
      });
    }

    /**
     * chatIdFromProps removed, reset state
     * - Do not fetch if submitting a new chat
     */
    if (
      chatId.current &&
      !chatIdFromProps &&
      !loadingConfiguration.submission
    ) {
      chatId.current = undefined;
      setBlocks([]);
    }
  }, [chatIdFromProps]);

  // Locates any failed user Message, updates "success" value to false, and inserts newly updated block into state.
  useEffect(() => {
    if (blocks.length && failedUserMessageId) {
      // Identify index of failed message
      const { failedMessageIndex, failedMessage } = findLastFailedUserMessage(
        blocks,
        failedUserMessageId
      );

      // Set success value to false in cloned block
      const updatedBlock = setUserMessageBlockFailed(failedMessage);

      // Replaces original failed message block with updated block.
      setBlocks((currentBlocks) =>
        spliceBlocks(currentBlocks, failedMessageIndex, updatedBlock)
      );
    }
  }, [failedUserMessageId]);

  const handleGetChat = async (
    chatIdToFetch: string,
    interruptedChatId?: string
  ): Promise<void> => {
    try {
      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        fetchChatById: true,
      });
      const response = await getChatMutation({
        chatId: chatIdToFetch,
        interruptedChatId,
      });
      const isInterruptedChat =
        !!interruptedChatId && chatIdToFetch === interruptedChatId;

      const { blocks } = response.data;

      setBlocks(blocks);
      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        fetchChatById: false,
      });

      if (isInterruptedChat) {
        /**
         * Find the last message sent by the User
         * and resend it.
         */
        const lastUserMessage = blocks
          .slice()
          .reverse()
          .find(
            (block) =>
              block.type === WiseOwlBlockType.Message &&
              block.values.role === MessageBlockRole.User
          ) as MessageBlock;

        await handleSendChatMessage({
          chatId: chatIdToFetch,
          input: lastUserMessage?.values.message,
        });
      }

      if (onEvent) {
        onEvent({
          eventType: WiseOwlEventEnum.getChat,
          data: {
            chatId: chatIdToFetch,
            /**
             * If the chat was interrupted, we set resumeChat to true
             * so that the consuming application can handle the navigation
             * to the chat page.
             */
            resumeChat: isInterruptedChat,
          },
        });
      }
      setInterruptedChatId(undefined);
      chatId.current = chatIdToFetch;
    } catch (err) {
      if (onError) {
        onError({
          eventType: WiseOwlEventEnum.getChat,
          data: { chatId: chatIdToFetch },
          error: err as Error,
        });
      }

      /**
       * TODO: Proper messaging/handling on chatId fetch error
       * It's likely an error occurs here because the chatId is not found
       * or doesn't belong to the current user. In this case, we reset the
       * chatId and navigate to the home page under the hood
       */
      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        fetchChatById: false,
      });
      chatId.current = undefined;
    }
  };

  const handleOnInitializeStream = (
    documentData: WiseOwlSyncDocumentContent
  ): void => {
    const { blocks: newBlocks } = documentData;

    if (onEvent) {
      onEvent({
        eventType: WiseOwlEventEnum.streamInitialize,
        data: {
          chatId: chatId.current,
        },
      });
    }

    setNewAiMessageStatus(NewAiMessageStatus.Loading);

    /**
     * The document will have some initial content to render on sync
     * instantiation so we want to render that before the stream starts
     */

    setBlocks((currentBlocks) => currentBlocks.concat(newBlocks));
  };

  const handleOnStream = (documentData: WiseOwlSyncDocumentContent): void => {
    const { blocks: newBlocks } = documentData;

    setBlocks(previousBlocks.current.concat(newBlocks));
  };

  const handleOnStreamComplete = (
    documentData: WiseOwlSyncDocumentContent
  ): void => {
    const { blocks: newBlocks } = documentData;

    if (onEvent) {
      onEvent({
        eventType: WiseOwlEventEnum.streamComplete,
        data: {
          chatId: chatId.current,
        },
      });
    }

    /**
     * Here, we update the ref with the updatedBlocks and add any new
     * blocks returned from the stream at the end
     */
    setBlocks(previousBlocks.current.concat(newBlocks));
    updateLoadingConfiguration({
      ...constants.defaultLoadingState,
      submission: false,
    });
    setNewAiMessageStatus(NewAiMessageStatus.Complete);
  };

  const handleOnStreamError = (): void => {
    if (onError) {
      onError({
        eventType: WiseOwlEventEnum.streamError,
        data: { chatId: chatId.current },
        error: Error("Document sync failed"),
      });
    }
    updateLoadingConfiguration({
      ...constants.defaultLoadingState,
      submission: false,
    });
    setNewAiMessageStatus(NewAiMessageStatus.None);
  };

  const { handleDocumentSync } =
    useTwilioSyncDocument<WiseOwlSyncDocumentContent>({
      onComplete: handleOnStreamComplete,
      onError: handleOnStreamError,
      onInitialize: handleOnInitializeStream,
      onStream: handleOnStream,
    });

  // consider moving to helpers
  const generateNewUserMessage = (
    input: string,
    createChatOnRetry: boolean
  ): MessageBlock => {
    const newUserMessage: MessageBlock = createNewUserMessageBlock(
      input,
      createChatOnRetry
    );

    setBlocks((currentBlocks) => {
      /**
       * Deep-copy blocks up to this point. Streaming will write blocks on-top of this
       * state.
       */
      const newBlocks = cloneDeep(currentBlocks).concat(newUserMessage);

      /**
       * previousBlocks is officially "preStreamingBlocks". We 'freeze' our existing block state,
       * and then we're safe to write all incoming block(s) from a message's SyncDoc.
       *
       * see handleOnStream to see how we're doing this.
       */
      previousBlocks.current = newBlocks;
      return newBlocks;
    });
    return newUserMessage;
  };

  const handleStartNewChat = async (
    data: SendMessageFormValues
  ): Promise<void> => {
    try {
      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        submission: true,
      });

      const createChatOnRetry = true;
      const { input } = data;
      const newUserMessage = generateNewUserMessage(input, createChatOnRetry);

      const { success, data: response } = await createChatMutation(input);
      const { accessToken, syncDocumentSid, chatId: newChatId } = response;
      const {
        values: { retryId },
      } = newUserMessage;

      if (!success) {
        setFailedUserMessageId(retryId);
        setChatSubmissionDisabled(true);
        throw new Error("Chat creation failed");
      }

      if (onEvent) {
        onEvent({
          eventType: WiseOwlEventEnum.startNewChat,
          data: {
            chatId: newChatId,
            syncDocumentSid,
          },
        });
      }

      setChatSubmissionDisabled(false);
      chatId.current = newChatId;
      handleDocumentSync({
        accessToken,
        syncDocumentSid,
      });
    } catch (err) {
      if (onError) {
        onError({
          eventType: WiseOwlEventEnum.startNewChat,
          error: err as Error,
          data: {},
        });
      }

      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        submission: false,
      });
    }
  };

  const cleanupBlocksForRegeneration = (
    blocks: WiseOwlBlock[]
  ): WiseOwlBlock[] => {
    return blocks.filter(
      (block) =>
        (block as MessageBlock).values.completionStatus !==
        MessageCompletionStatus.Error
    );
  };

  const handleSendChatMessage = async (
    data: SendMessageFormValues
  ): Promise<void> => {
    try {
      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        submission: true,
      });
      setBlocks((currentBlocks) => cleanupBlocksForRegeneration(currentBlocks));
      const createChatOnRetry = false;
      const newUserMessage = generateNewUserMessage(
        data.input,
        createChatOnRetry
      );
      const {
        values: { retryId },
      } = newUserMessage;

      if (data.chatId) {
        const { success, data: response } = await sendMessageMutation({
          chatId: data.chatId,
          input: data.input,
        });

        if (!success) {
          setFailedUserMessageId(retryId);
          throw new Error("Message send failed");
        }

        const { accessToken, syncDocumentSid } = response;

        if (onEvent) {
          onEvent({
            eventType: WiseOwlEventEnum.sendChatMessage,
            data: {
              chatId: data.chatId,
              syncDocumentSid,
            },
          });
        }

        handleDocumentSync({
          accessToken,
          syncDocumentSid,
        });
      }
    } catch (err) {
      if (onError) {
        onError({
          eventType: WiseOwlEventEnum.sendChatMessage,
          error: err as Error,
          data: {
            chatId: data.chatId,
          },
        });
      }
      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        submission: false,
      });
    }
  };

  const handleSubmitFeedback = async (
    data: CustomerFeedbackFormValues
  ): Promise<void> => {
    try {
      const { messageId, feedback, positive } = data;

      if (chatId.current) {
        const { success } = await createFeedbackMutation({
          chatId: chatId.current,
          messageId,
          feedback,
          positive,
        });

        if (!success) {
          throw new Error("Create feedback failed");
        }

        if (onEvent) {
          onEvent({
            eventType: WiseOwlEventEnum.submitFeedback,
            data: {
              ...data,
              positive: positive ? "positive" : "negative",
              chatId: chatId.current,
            },
          });
        }
      }
    } catch (err) {
      if (onError) {
        onError({
          eventType: WiseOwlEventEnum.submitFeedback,
          error: err as Error,
          data: { chatId: chatId.current },
        });
      }
      throw err;
    }
  };

  const handleRedirectAction = (redirectUrl: string): void => {
    if (onEvent) {
      onEvent({
        eventType: WiseOwlEventEnum.redirect,
        data: { chatId: chatId.current, redirectUrl },
      });
    }
  };

  const handleMessageRetry = (retryId: string): void => {
    const { failedMessageIndex, failedMessage } = findLastFailedUserMessage(
      blocks,
      retryId
    );

    setBlocks((currentBlocks) =>
      spliceBlocks(currentBlocks, failedMessageIndex)
    );

    if (failedMessage.values.createChatOnRetry) {
      handleStartNewChat({ input: failedMessage.values.message });

      if (onEvent) {
        onEvent({
          eventType: WiseOwlEventEnum.startNewChatRetry,
          data: { retryId },
        });
      }
    } else {
      handleSendChatMessage({
        input: failedMessage.values.message,
        chatId: chatId.current,
      });

      if (onEvent) {
        onEvent({
          eventType: WiseOwlEventEnum.sendChatMessageRetry,
          data: { retryId },
        });
      }
    }
  };

  const removeBlockAtIndex = (
    blocks: WiseOwlBlock[],
    blockToRemoveIndex: number
  ): WiseOwlBlock[] => {
    blocks.splice(blockToRemoveIndex, 1);
    return blocks;
  };

  const handleResponseRegeneration = (regenerationId: string): void => {
    const [failedUserPromptMessage, failedUserPromptIndex] =
      findUserPromptMessageToRegenerateAssistantMessage(blocks, regenerationId);
    setBlocks((currentBlocks) =>
      removeBlockAtIndex(currentBlocks, failedUserPromptIndex)
    );
    handleSendChatMessage({
      input: failedUserPromptMessage.values.message,
      chatId: chatId.current,
    });
    updateLoadingConfiguration({
      ...constants.defaultLoadingState,
      submission: false,
    });
  };

  const handleLoginRedirect = () => {
    if (onEvent) {
      onEvent({
        eventType: WiseOwlEventEnum.loginRedirect,
        data: {
          chatId: chatId.current,
        },
      });
    }

    if (onTrackEvent) {
      onTrackEvent({
        eventName: trackingDetails.wiseOwlFeedbackLogin,
      });
    }
  };

  const handleTrackEvent = (
    eventName: string,
    additionalData: Record<string, unknown>
  ): void => {
    if (onTrackEvent) {
      onTrackEvent({
        eventName,
        additionalData,
      });
    }
  };

  const handleGetChatSummary = async (): Promise<
    WiseOwlGetChatSummaryByIdResponseData | undefined
  > => {
    try {
      if (chatId.current) {
        const { success, data: response } = await getChatSummaryMutation(
          chatId.current
        );

        const { subject, description, wasTranslated } = response;

        if (!success) {
          throw new Error("Chat summary failed");
        }

        if (onEvent) {
          onEvent({
            eventType: WiseOwlEventEnum.getChatSummary,
            data: {
              chatId: chatId.current,
            },
          });
        }

        return { subject, description, wasTranslated };
      }
    } catch (err) {
      if (onError) {
        onError({
          eventType: WiseOwlEventEnum.getChatSummary,
          error: err as Error,
          data: {
            chatId: chatId.current,
          },
        });
      }
    }
  };

  // init WO Context
  const context = {
    applicationProps: props,
    ingress,
    chatId: chatId.current,
    loadingConfiguration,
    isLoggedIn: !stargateUngatedUserId,
    author: `${user.firstName} ${user.lastName}`,
    handlers: {
      handleSendChatMessage,
      handleStartNewChat,
      handleSubmitFeedback,
      handleRedirectAction,
      handleLoginRedirect,
      handleTrackEvent,
      handleMessageRetry,
      handleResponseRegeneration,
      handleGetChatSummary,
    },
  };

  return (
    <WiseOwlProvider context={context}>
      <Box element={constants.wiseOwlElementIds.WISE_OWL_CONTAINER}>
        <WiseOwlChat
          loadingConfiguration={loadingConfiguration}
          chatId={chatId.current}
          chatHistory={[]}
          blocks={blocks}
          newAiMessageStatus={newAiMessageStatus}
          onStartNewChat={handleStartNewChat}
          onSendChatMessage={handleSendChatMessage}
          onSubmitFeedback={handleSubmitFeedback}
          handleRetry={handleMessageRetry}
          handleResponseRegeneration={handleResponseRegeneration}
          disabled={disableChatMessageSubmission}
        />

        <LegalDisclaimer />
      </Box>
    </WiseOwlProvider>
  );
};

export default WiseOwl;
